import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { TextArea } from '@/components/TextArea';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { SingleDatePicker } from '@/components/SingleDatePicker';
import { TimePicker } from 'antd';
import styles from './Regularization.module.scss';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import {
  FiUsers,
  FiFolder,
  FiList,
  FiGlobe,
  FiFileText,
  FiSave,
} from 'react-icons/fi';
import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import { InitialsBadge } from '@/components/InitialsBadge';
import {
  IconBadge,
  iconBadgeSizes,
  iconBadgeShapes,
} from '@/components/IconBadge';
import { toDateValue } from '@/utils/firestoreDate';
import {
  getProjectDropdownOptions,
  getTaskDropdownOptions,
  getManagerDropdownOptions,
  getDefaultTimezone,
  calculateDuration,
  validateForm,
  getHoursMinutes,
  getInitialFormData,
  getTeamDropdownOptions,
  check24HoursLimit,
  canUseRegularizationDateSelection,
  buildRegularizationFormDateChange,
  managersFromMemberDetails,
} from './Regularization.helper';
import { useDispatch, useSelector } from 'react-redux';
import { Button, buttonVariants } from '@/components/Button';
import { regularizationActionTypes } from '@/store/Regularization/Regularization.actionTypes';
import { useTimezones } from '@/hooks/useTimezones';
import { useUserTeamsForCompany } from '@/hooks/useUserTeamsForCompany';
import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';

const timePickerPopupProps = {
  getPopupContainer: () => document.body,
  popupStyle: { zIndex: 1200 },
};

const TASK_DETAILS_MAX_LENGTH = 250;

function formatManagerOptionLabel(option) {
  const label = option.userName || option.label;
  return (
    <div className={styles.personCell}>
      <InitialsBadge label={label} twoLetter />
      <div className={styles.personCellText}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          {label}
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          {option.role || '—'}
        </Text>
      </div>
    </div>
  );
}

const getRowStartDate = (rowData) => {
  const direct = toDateValue(rowData?.startTime);
  if (direct) return direct;

  if (
    typeof rowData?.startTime === 'string' &&
    rowData?.startHours != null &&
    rowData?.startMinutes != null
  ) {
    const base = new Date(rowData.startTime);
    if (Number.isNaN(base.getTime())) return null;
    base.setHours(
      Number(rowData.startHours),
      Number(rowData.startMinutes),
      0,
      0
    );
    return base;
  }

  return null;
};

const getRowEndDate = (rowData, startDate) => {
  const direct = toDateValue(rowData?.endTime);
  if (direct) return direct;

  if (
    startDate &&
    rowData?.endTime?.endHours != null &&
    rowData?.endTime?.endMinutes != null
  ) {
    const end = new Date(startDate);
    end.setHours(
      Number(rowData.endTime.endHours),
      Number(rowData.endTime.endMinutes),
      0,
      0
    );
    return end;
  }

  return null;
};

export const UpdateRegularizationModal = ({
  isVisible,
  rowData,
  teamProjects,
  teams,
  fetchProjectTasks,
  fetchRegulationRequests,
  onClose,
  onBusyChange,
}) => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.auth);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { teams: userTeamsList } = useUserTeamsForCompany(
    profile?.id,
    activeCompany?.companyId
  );
  const [taskLists, setTaskLists] = useState([]);

  const [formData, setFormData] = useState(
    getInitialFormData(getDefaultTimezone())
  );
  const activeTeamId =
    teams.active?.activeTeamid ?? teams.active?.activeTeamId ?? null;
  const teamIdForManagers =
    formData.team?.value ?? rowData?.teamId ?? activeTeamId;
  const { memberDetailsUnfiltered } =
    useTeamMemberDetailsForSelect(teamIdForManagers);
  const availableManagers = useMemo(
    () => managersFromMemberDetails(memberDetailsUnfiltered),
    [memberDetailsUnfiltered]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const setBusy = (busy) => {
    if (typeof onBusyChange === 'function') {
      onBusyChange(busy);
    }
  };
  const projectDropdownOptions = useMemo(() => {
    return getProjectDropdownOptions(teams);
  }, [teams?.projects]);
  const taskDropdownOptions = useMemo(() => {
    return getTaskDropdownOptions(taskLists);
  }, [taskLists]);
  const managerDropdownOptions = useMemo(() => {
    return getManagerDropdownOptions(availableManagers);
  }, [availableManagers]);
  const teamDropdownOptions = useMemo(
    () => getTeamDropdownOptions(userTeamsList),
    [userTeamsList]
  );
  const timezoneDropdownOptions = useTimezones();

  useEffect(() => {
    if (!rowData) return;
    const project = teamProjects.find((p) => p.id === rowData.projectId);
    const startDate = getRowStartDate(rowData);
    const endDate = getRowEndDate(rowData, startDate);
    const selectedDate = startDate
      ? (() => {
          const d = new Date(startDate);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const selectedManager = managerDropdownOptions.find(
      (m) => m.value === rowData.assignee
    );

    const teamSelection = rowData.teamId
      ? (() => {
          const fromList = userTeamsList.find(
            (x) => x.teamId === rowData.teamId
          );
          return {
            value: rowData.teamId,
            label: fromList?.teamName || rowData.teamName || rowData.teamId,
          };
        })()
      : null;

    setFormData({
      team: teamSelection,
      projectId: project
        ? {
            ...project,
            label: project.data?.project_name,
            value: project.id,
          }
        : null,
      projectName: project?.data?.project_name || '',
      taskId: rowData.taskID || null,
      taskName: rowData.taskName || '',
      taskDetails: rowData.taskDetails || '',
      assignee: selectedManager || '',
      date: selectedDate,
      startTime: startDate ? dayjs(startDate) : null,
      endTime: endDate ? dayjs(endDate) : null,
      timezone: rowData.timezone
        ? {
            value: rowData.timezone,
            label: rowData.timezone,
          }
        : {
            value: getDefaultTimezone(),
            label: getDefaultTimezone(),
          },
    });
    if (!rowData.projectId) {
      setTaskLists([]);
      return;
    }

    fetchProjectTasks(rowData.projectId, (fetchedTasks) => {
      setTaskLists(fetchedTasks);
      const selectedTask = fetchedTasks.find((t) => t.id === rowData.taskID);
      setFormData((prev) => ({
        ...prev,
        taskId: selectedTask
          ? {
              ...selectedTask,
              label: selectedTask.Title,
              value: selectedTask.id,
            }
          : null,
      }));
    });
  }, [rowData, fetchProjectTasks, managerDropdownOptions, userTeamsList]);

  useEffect(() => {
    if (!teamIdForManagers || !availableManagers.length) return;
    setFormData((prev) => {
      const opts = getManagerDropdownOptions(availableManagers);
      const uid = prev.assignee?.userUID ?? prev.assignee?.value;
      if (uid && opts.some((o) => o.value === uid)) return prev;
      return { ...prev, assignee: '' };
    });
  }, [teamIdForManagers, availableManagers]);

  const handleTeamChange = (team) => {
    setFormData((prev) => ({
      ...prev,
      team,
      assignee: '',
    }));
  };
  const handleProjectChange = (project) => {
    setFormData((prev) => ({
      ...prev,
      projectId: project,
      projectName: project.label,
      taskId: null,
      taskName: '',
    }));
    fetchProjectTasks(project.id, (fetchedTasks) => setTaskLists(fetchedTasks));
  };
  const handleTaskChange = (task) => {
    setFormData((prev) => ({
      ...prev,
      taskId: task,
      taskName: task.label,
    }));
  };
  const handleAssigneeChange = (assignee) => {
    setFormData((prev) => ({
      ...prev,
      assignee: assignee,
    }));
  };
  const handleDateChange = (date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, date: null }));
      return;
    }
    if (!canUseRegularizationDateSelection(date)) return;
    setFormData((prev) => buildRegularizationFormDateChange(prev, date));
  };
  const handleTimezoneChange = (timezone) => {
    setFormData((prev) => ({
      ...prev,
      timezone: timezone,
    }));
  };
  const handleStartTimeChange = (startTime) => {
    setFormData((prev) => ({
      ...prev,
      startTime: startTime,
    }));
  };
  const handleEndTimeChange = (endTime) => {
    setFormData((prev) => ({
      ...prev,
      endTime: endTime,
    }));
  };
  const duration = useMemo(() => {
    const duration = calculateDuration(formData.startTime, formData.endTime);
    return {
      isValid: duration.isValid,
      hoursText: String(duration.hours || 0).padStart(2, '0'),
      minutesText: String(duration.minutes || 0).padStart(2, '0'),
    };
  }, [formData.startTime, formData.endTime]);

  const handleSubmit = async () => {
    if (submitInFlightRef.current || isSubmitting) return;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setBusy(true);
    const isValid = validateForm(formData);
    if (!isValid) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }
    const timeLimitCheck = await check24HoursLimit(formData, teams, profile);
    if (!timeLimitCheck) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }

    const duration = calculateDuration(formData.startTime, formData.endTime);
    const dateStr = dayjs(formData.date).format('YYYY-MM-DD');
    const { hours: startHours, minutes: startMinutes } = getHoursMinutes(
      formData.startTime
    );
    const { hours: endHours, minutes: endMinutes } = getHoursMinutes(
      formData.endTime
    );

    const updateData = {
      userId: profile.id,
      userName:
        (profile?.name && String(profile.name).trim()) || profile?.email || '',
      companyID: activeCompany.companyId,
      projectId: formData.projectId?.id ?? null,
      taskName: formData.taskName || '',
      taskID: formData.taskId?.id ?? null,
      taskDetails: formData.taskDetails || '',
      assignee: formData.assignee?.userUID,
      taskHours: duration.hours,
      taskMinutes: duration.minutes,
      status: 'Pending',
      typeLog: 'Manual',
      startTime: dateStr,
      requestDate: new Date(),
      endTime: { endHours, endMinutes },
      startHours,
      startMinutes,
      timezone: formData.timezone?.value || getDefaultTimezone(),
      teamId: formData.team?.value ?? null,
      teamName: formData.team?.label ?? null,
    };

    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.UPDATE_REQUEST,
        payload: {
          requestId: rowData.id,
          updateData,
          companyId: activeCompany.companyId,
          oldAssignee: rowData.assignee,
          newAssignee: formData.assignee?.userUID || rowData.assignee,
          onComplete: resolve,
        },
      });
    });
    if (result?.success) {
      onClose();
      setTimeout(() => {
        fetchRegulationRequests();
      }, 100);
    }
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    setBusy(false);
  };
  if (!rowData) return null;

  const dateString = formData.date
    ? moment(formData.date).format('YYYY-MM-DD')
    : null;

  return (
    <Dialog
      visible={isVisible}
      onHide={onClose}
      closable={!isSubmitting}
      closeOnEscape={!isSubmitting}
      dismissableMask={!isSubmitting}
      style={{ width: '680px' }}
      header={
        <div className={styles.regDialogHeader}>
          <IconBadge
            icon={<FiFileText size={20} color="var(--indigo-600)" />}
            bg="indigo-100"
            size={iconBadgeSizes.lg}
            shape={iconBadgeShapes.circle}
          />
          <div className={styles.regDialogHeaderText}>
            <Text
              variant={textVariantsEnum.Text_lg}
              fontWeight={fontWeightEnum.Semibold}
            >
              Update Regularization Request
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Update the details of the task you want to regularize.
            </Text>
          </div>
        </div>
      }
      footer={
        <div className={styles.regDialogFooter}>
          <Button
            text="Update Request"
            icon={<FiSave size={16} />}
            onClick={handleSubmit}
            variant={buttonVariants.primary}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      }
    >
      <div className={styles.regFormContent}>
        <div className={styles.formRow2Col}>
          <Dropdown
            options={teamDropdownOptions}
            value={formData.team}
            label="Department"
            selectStyles={borderedSelectStyles}
            leftIcon={<FiUsers size={14} color="var(--indigo-500)" />}
            onChange={handleTeamChange}
            placeholder="Select Department"
          />
          <Dropdown
            options={projectDropdownOptions}
            value={formData.projectId}
            label="Project"
            selectStyles={borderedSelectStyles}
            leftIcon={<FiFolder size={14} color="var(--indigo-500)" />}
            onChange={handleProjectChange}
            placeholder="Select Project"
          />
        </div>

        <div className={styles.formRow2Col}>
          <Dropdown
            options={taskDropdownOptions}
            value={formData.taskId}
            label="Task"
            selectStyles={borderedSelectStyles}
            leftIcon={<FiList size={14} color="var(--indigo-500)" />}
            onChange={handleTaskChange}
            placeholder="Select Task"
          />
          <TextArea
            value={formData.taskDetails}
            label="Task Details"
            placeholder="Describe the task in detail"
            rows={2}
            maxLength={TASK_DETAILS_MAX_LENGTH}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                taskDetails: e.target.value,
              }));
            }}
          />
        </div>

        <div className={styles.formRow2Col}>
          <div className={styles.datePicker}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.label}
            >
              Date
            </Text>
            <SingleDatePicker
              compact
              className={styles.regDatePickerTrigger}
              date={dateString}
              onDateChange={(str) =>
                handleDateChange(
                  str ? moment(str, 'YYYY-MM-DD').toDate() : null
                )
              }
              placeholder="Select date"
            />
          </div>
          <Dropdown
            options={timezoneDropdownOptions}
            value={formData.timezone}
            label="Timezone"
            selectStyles={borderedSelectStyles}
            leftIcon={<FiGlobe size={14} color="var(--indigo-500)" />}
            onChange={handleTimezoneChange}
            placeholder="Select Timezone"
          />
        </div>

        <div className={styles.formRow2Col}>
          <div className={styles.timePicker}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.label}
            >
              Task Start Time
            </Text>
            <TimePicker
              use12Hours
              showSecond={false}
              value={formData.startTime}
              className={styles.timePickerInput}
              onChange={handleStartTimeChange}
              placeholder="Select start time"
              {...timePickerPopupProps}
            />
          </div>
          <div className={styles.timePicker}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              className={styles.label}
            >
              Task End Time
            </Text>
            <TimePicker
              use12Hours
              showSecond={false}
              value={formData.endTime}
              className={styles.timePickerInput}
              onChange={handleEndTimeChange}
              placeholder="Select end time"
              {...timePickerPopupProps}
            />
          </div>
        </div>

        <div className={styles.durationRow}>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
            className={styles.label}
          >
            Duration
          </Text>
          <div className={styles.durationBoxes}>
            <Input
              value={duration.hoursText}
              readOnly
              rightBlock={
                <Text variant={textVariantsEnum.Text_xs} color="gray-400">
                  hours
                </Text>
              }
            />
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Medium}
              color="gray-400"
            >
              :
            </Text>
            <Input
              value={duration.minutesText}
              readOnly
              rightBlock={
                <Text variant={textVariantsEnum.Text_xs} color="gray-400">
                  minutes
                </Text>
              }
            />
          </div>
        </div>

        <Dropdown
          options={managerDropdownOptions}
          value={formData.assignee}
          label="Manager"
          selectStyles={borderedSelectStyles}
          formatOptionLabel={formatManagerOptionLabel}
          onChange={handleAssigneeChange}
          placeholder="Select Manager"
        />
      </div>
    </Dialog>
  );
};
